import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import supabase, { isSupabaseConfigured } from '../supabase/supabase';
import { sendConfirmationEmail } from '../services/emailService';

export const AuthContext = createContext();

const ADMIN_EMAILS = ['admin@dayclap.com'];
const SITE_URL = 'https://dayclap.com';
const AUTH_TIMEOUT_MS = 30000;

const debugLogs = [];
const MAX_DEBUG_LOGS = 50;
let debugListeners = [];

const addDebugLog = (level, message, data = null) => {
  const entry = {
    id: Date.now() + Math.random(),
    timestamp: new Date().toLocaleTimeString('en-US', {
      hour12: false, hour: '2-digit', minute: '2-digit',
      second: '2-digit', fractionalSecondDigits: 3
    }),
    level,
    message,
    data: data ? (typeof data === 'string' ? data : JSON.stringify(data, null, 2)) : null
  };
  debugLogs.push(entry);
  if (debugLogs.length > MAX_DEBUG_LOGS) debugLogs.shift();
  debugListeners.forEach(fn => fn(entry));
};

export const subscribeDebug = (fn) => {
  debugListeners.push(fn);
  return () => { debugListeners = debugListeners.filter(l => l !== fn); };
};

export const getDebugLogs = () => [...debugLogs];
export const clearDebugLogs = () => {
  debugLogs.length = 0;
  debugListeners.forEach(fn => fn({ id: 'clear', level: 'clear' }));
};

const generateToken = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return Math.random().toString(36).substring(2) + Date.now().toString(36) + Math.random().toString(36).substring(2);
};

const withTimeout = (promise, ms, name = 'operation') => {
  let tid;
  const timeout = new Promise((_, reject) => {
    tid = setTimeout(() => reject(new Error(`TIMEOUT: ${name} exceeded ${ms / 1000}s`)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(tid));
};

const delay = (ms) => new Promise(r => setTimeout(r, ms));

const normalizeError = (err) => {
  const msg = err?.message || String(err);
  const status = err?.status || err?.statusCode || null;
  const code = err?.code || null;
  const lower = msg.toLowerCase();

  addDebugLog('warn', `normalizeError: "${msg}" (status: ${status}, code: ${code})`);

  if (lower.includes('aborted') || lower.includes('timeout:')) {
    return { message: 'Request timed out. Please check your connection and try again.', isTimeout: true };
  }
  if (status === 429 || code === 'over_email_send_rate_limit' || lower.includes('rate limit')) {
    return { message: 'Too many signup attempts. Please wait a minute and try again.', isRateLimited: true };
  }
  if (lower.includes('already registered') || lower.includes('user already exists') || lower.includes('already been registered')) {
    return { message: 'This email is already registered. Please log in instead.', isAlreadyRegistered: true };
  }
  if (lower.includes('email not confirmed') || lower.includes('email_not_confirmed')) {
    return { message: 'Please verify your email address before logging in.', needsEmailVerification: true };
  }
  if (lower.includes('invalid login credentials') || lower.includes('invalid credentials')) {
    return { message: 'Invalid email or password. Please try again.' };
  }
  if (status === 504 || status === 502 || status === 503 || lower.includes('failed to fetch') || lower.includes('network')) {
    return { message: 'Network error. Please check your connection and try again.', isNetworkError: true };
  }
  return { message: msg };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [configError] = useState(!isSupabaseConfigured());
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  const userRef = useRef(user);
  const profileRef = useRef(profile);
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { profileRef.current = profile; }, [profile]);

  const checkIsAdminEmail = useCallback((email) => {
    return ADMIN_EMAILS.includes(email?.toLowerCase());
  }, []);

  const fetchProfile = useCallback(async (userId, userEmail, userMetadata = {}) => {
    if (!supabase) return null;
    try {
      addDebugLog('info', `Fetching profile for ${userId}`);
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error && error.code === 'PGRST116') {
        addDebugLog('info', 'Profile not found, creating via fetchProfile');
        const name = userMetadata?.name || 'User';
        const avatarUrl = userMetadata?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random`;

        const { data: newProfile, error: insertError } = await supabase
          .from('user_profiles')
          .insert([{ id: userId, name, email: userEmail, avatar_url: avatarUrl }])
          .select()
          .single();

        if (!insertError && newProfile) {
          const isAdminEmail = checkIsAdminEmail(userEmail);
          const effectiveRole = isAdminEmail ? 'admin' : newProfile.role;
          const effectiveProfile = { ...newProfile, role: effectiveRole };
          setProfile(effectiveProfile);
          setIsAdmin(effectiveRole === 'admin');
          setNeedsOnboarding(!effectiveProfile.onboarding_complete && effectiveRole !== 'admin');
          return effectiveProfile;
        }
        if (insertError) addDebugLog('error', `Profile insert failed: ${insertError.message}`);
      }

      if (error && error.code !== 'PGRST116') {
        addDebugLog('error', `Profile fetch error: ${error.message}`);
        if (checkIsAdminEmail(userEmail)) {
          const vp = { id: userId, name: 'Admin', email: userEmail, role: 'admin', avatar_url: '' };
          setProfile(vp);
          setIsAdmin(true);
          setNeedsOnboarding(false);
          return vp;
        }
        return null;
      }

      if (data) {
        const isAdminEmail = checkIsAdminEmail(userEmail || data.email);
        const effectiveRole = isAdminEmail ? 'admin' : data.role;
        const effectiveProfile = { ...data, role: effectiveRole };
        setProfile(effectiveProfile);
        setIsAdmin(effectiveRole === 'admin');
        setNeedsOnboarding(!effectiveProfile.onboarding_complete && effectiveRole !== 'admin');
        return effectiveProfile;
      }

      return null;
    } catch (err) {
      addDebugLog('error', `fetchProfile exception: ${err.message}`);
      return null;
    }
  }, [checkIsAdminEmail]);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    addDebugLog('info', 'AuthProvider initializing...');

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        addDebugLog('info', `Session found: ${session.user.email}`);
        setUser(session.user);
        await fetchProfile(session.user.id, session.user.email, session.user.user_metadata);
      } else {
        addDebugLog('info', 'No existing session');
      }
      setLoading(false);
    }).catch((err) => {
      addDebugLog('error', `getSession error: ${err.message}`);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      addDebugLog('info', `Auth state: ${event}`);
      if (session?.user) {
        setUser(session.user);
        await fetchProfile(session.user.id, session.user.email, session.user.user_metadata);
      } else {
        setUser(null);
        setProfile(null);
        setIsAdmin(false);
        setNeedsOnboarding(false);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  const login = useCallback(async (email, password) => {
    if (!supabase) return { success: false, error: 'Supabase is not configured.' };
    addDebugLog('info', `Login attempt: ${email}`);
    try {
      const { data, error } = await withTimeout(
        supabase.auth.signInWithPassword({ email, password }),
        AUTH_TIMEOUT_MS, 'Login'
      );

      if (error) {
        addDebugLog('error', `Login error: ${error.message}`);
        const normalized = normalizeError(error);
        return { success: false, error: normalized.message, ...normalized };
      }

      if (data?.user) {
        const userEmail = data.user.email || email;
        const profileData = await fetchProfile(data.user.id, userEmail, data.user.user_metadata);

        if (checkIsAdminEmail(userEmail)) {
          setIsAdmin(true);
          if (!profileData) {
            const vp = { id: data.user.id, name: 'Admin', email: userEmail, role: 'admin', avatar_url: '' };
            setProfile(vp);
          }
        }

        setUser(data.user);
        setLoading(false);
        addDebugLog('success', `Login successful`);
      }

      return { success: true, data, profile: profileRef.current };
    } catch (err) {
      addDebugLog('error', `Login exception: ${err.message}`);
      const normalized = normalizeError(err);
      return { success: false, error: normalized.message, ...normalized };
    }
  }, [fetchProfile, checkIsAdminEmail]);

  const signup = useCallback(async (name, email, password) => {
    if (!supabase) return { success: false, error: 'Supabase is not configured.' };

    addDebugLog('info', `Signup attempt: ${email} (name: ${name})`);

    try {
      addDebugLog('info', 'Calling supabase.auth.signUp()...');
      const startTime = Date.now();

      const { data, error } = await withTimeout(
        supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              name,
              avatar_url: `https://ui-avatars.com/api/?name=${encodeURIComponent(name || email)}&background=random`
            }
          }
        }),
        AUTH_TIMEOUT_MS, 'Sign up'
      );

      const elapsed = Date.now() - startTime;
      addDebugLog('info', `signUp() returned after ${elapsed}ms`);

      if (error) {
        addDebugLog('error', `signUp error: "${error.message}" (status: ${error.status})`);
        const normalized = normalizeError(error);
        return { success: false, error: normalized.message, ...normalized };
      }

      addDebugLog('success', `signUp succeeded! User: ${data?.user?.id}`);

      if (!data?.user) {
        return { success: false, error: 'Signup succeeded but no user returned. Please try logging in.' };
      }

      const confirmationToken = generateToken();
      const userId = data.user.id;
      const avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(name || email)}&background=random`;

      addDebugLog('info', `Generated token: ${confirmationToken.substring(0, 8)}...`);

      addDebugLog('info', 'Saving profile with token via RPC (SECURITY DEFINER)...');

      let tokenSaved = false;

      try {
        const { data: rpcData, error: rpcErr } = await supabase.rpc('save_profile_with_token', {
          p_user_id: userId,
          p_name: name || '',
          p_email: email,
          p_avatar_url: avatarUrl,
          p_confirmation_token: confirmationToken
        });

        if (rpcErr) {
          addDebugLog('error', `RPC error: ${rpcErr.message}`);
        } else if (rpcData?.success) {
          addDebugLog('success', `Token saved and verified via RPC!`);
          tokenSaved = true;
        } else {
          addDebugLog('error', `RPC returned failure: ${JSON.stringify(rpcData)}`);
        }
      } catch (rpcException) {
        addDebugLog('error', `RPC exception: ${rpcException.message}`);
      }

      if (!tokenSaved) {
        addDebugLog('warn', 'RPC failed — trying direct upsert as fallback...');

        for (let i = 0; i < 5; i++) {
          const { data: sessionData } = await supabase.auth.getSession();
          if (sessionData?.session) {
            addDebugLog('info', `Session established after ${(i + 1) * 500}ms`);
            break;
          }
          await delay(500);
        }

        for (let attempt = 1; attempt <= 3; attempt++) {
          addDebugLog('info', `Upsert attempt ${attempt}/3...`);

          const { error: upsertError } = await supabase
            .from('user_profiles')
            .upsert({
              id: userId,
              name: name || '',
              email: email,
              avatar_url: avatarUrl,
              confirmation_token: confirmationToken,
              confirmation_sent_at: new Date().toISOString(),
              email_confirmed_at: null
            });

          if (!upsertError) {
            addDebugLog('success', `Upsert succeeded on attempt ${attempt}`);
            tokenSaved = true;
            break;
          }

          addDebugLog('error', `Upsert attempt ${attempt} failed: ${upsertError.message}`);
          if (attempt < 3) await delay(attempt * 1000);
        }
      }

      if (tokenSaved) {
        addDebugLog('info', 'Verifying token in database...');
        try {
          const { data: verifyData, error: verifyError } = await supabase
            .from('user_profiles')
            .select('confirmation_token, email')
            .eq('id', userId)
            .single();

          if (verifyError) {
            addDebugLog('warn', `Verify query error: ${verifyError.message}`);
          } else if (verifyData?.confirmation_token === confirmationToken) {
            addDebugLog('success', `Token verified in database!`);
          } else {
            addDebugLog('error', `Token mismatch!`);
            tokenSaved = false;
          }
        } catch (verifyException) {
          addDebugLog('warn', `Verify exception: ${verifyException.message}`);
        }
      }

      await fetchProfile(userId, email, data.user.user_metadata);

      if (!tokenSaved) {
        addDebugLog('error', 'Token was NOT saved — cannot send confirmation email');
        return {
          success: false,
          error: 'Account created but we could not save your confirmation token. Please try signing up again.',
          tokenSaveFailed: true
        };
      }

      const confirmUrl = `${SITE_URL}/confirm-email?token=${confirmationToken}`;
      addDebugLog('info', `Sending confirmation email to ${email}...`);

      try {
        const emailResult = await sendConfirmationEmail(email, name, confirmUrl);
        if (emailResult.success) {
          addDebugLog('success', 'Confirmation email sent!');
        } else {
          addDebugLog('error', `Confirmation email failed: ${emailResult.error}`);
        }
      } catch (emailErr) {
        addDebugLog('error', `Email send exception: ${emailErr.message}`);
      }

      return { success: true, data, needsEmailConfirmation: true };
    } catch (err) {
      addDebugLog('error', `signUp exception: ${err.message}`);
      const normalized = normalizeError(err);
      return { success: false, error: normalized.message, ...normalized };
    }
  }, [fetchProfile]);

  const confirmEmail = useCallback(async (token) => {
    if (!supabase) return { success: false, error: 'Supabase is not configured.' };
    addDebugLog('info', `Confirming email with token: ${token?.substring(0, 8)}...`);

    if (!token || token.trim() === '') {
      return { success: false, error: 'No token provided.' };
    }

    try {
      const { data, error } = await supabase.rpc('confirm_email', { p_token: token.trim() });

      if (error) {
        addDebugLog('error', `confirm_email RPC error: ${error.message}`);

        if (error.message?.includes('could not find') || error.message?.includes('does not exist')) {
          const { data: profileData, error: queryError } = await supabase
            .from('user_profiles')
            .select('id, confirmation_token, email_confirmed_at')
            .eq('confirmation_token', token.trim())
            .single();

          if (queryError || !profileData) {
            return { success: false, error: 'Invalid or expired confirmation link.' };
          }

          if (profileData.email_confirmed_at) {
            return { success: true, message: 'Email already confirmed!' };
          }

          await supabase
            .from('user_profiles')
            .update({ email_confirmed_at: new Date().toISOString(), confirmation_token: null })
            .eq('id', profileData.id);

          return { success: true, message: 'Email confirmed successfully!' };
        }

        return { success: false, error: error.message };
      }

      const result = data || {};
      if (result.success) {
        const currentUser = userRef.current;
        if (currentUser) {
          await fetchProfile(currentUser.id, currentUser.email, currentUser.user_metadata);
        }
        return { success: true, message: result.message || 'Email confirmed!' };
      }

      return { success: false, error: result.error || 'Confirmation failed.' };
    } catch (err) {
      addDebugLog('error', `confirmEmail exception: ${err.message}`);
      return { success: false, error: err.message };
    }
  }, [fetchProfile]);

  const resendConfirmation = useCallback(async () => {
    const currentUser = userRef.current;
    const currentProfile = profileRef.current;

    if (!supabase || !currentUser || !currentProfile) {
      return { success: false, error: 'Not authenticated.' };
    }

    try {
      const newToken = generateToken();

      const { data: rpcData, error: rpcError } = await supabase.rpc('save_profile_with_token', {
        p_user_id: currentUser.id,
        p_name: currentProfile.name || '',
        p_email: currentProfile.email || currentUser.email,
        p_avatar_url: currentProfile.avatar_url || '',
        p_confirmation_token: newToken
      });

      if (rpcError || !rpcData?.success) {
        await supabase
          .from('user_profiles')
          .update({ confirmation_token: newToken, confirmation_sent_at: new Date().toISOString() })
          .eq('id', currentUser.id);
      }

      const confirmUrl = `${SITE_URL}/confirm-email?token=${newToken}`;
      const emailResult = await sendConfirmationEmail(currentProfile.email, currentProfile.name, confirmUrl);

      if (emailResult.success) {
        await fetchProfile(currentUser.id, currentProfile.email, currentUser.user_metadata);
        return { success: true };
      }

      return { success: false, error: emailResult.error || 'Failed to send email.' };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [fetchProfile]);

  const resetPassword = useCallback(async (email) => {
    if (!supabase) return { success: false, error: 'Supabase is not configured.' };
    try {
      const { error } = await withTimeout(
        supabase.auth.resetPasswordForEmail(email, { redirectTo: `${SITE_URL}/reset-password` }),
        AUTH_TIMEOUT_MS, 'Password reset'
      );
      if (error) return { success: false, error: error.message };
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const updatePassword = useCallback(async (newPassword) => {
    if (!supabase) return { success: false, error: 'Supabase is not configured.' };
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) return { success: false, error: error.message };
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const updateProfile = useCallback(async (updates) => {
    const currentUser = userRef.current;
    if (!supabase || !currentUser) return { success: false, error: 'Not authenticated.' };
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', currentUser.id)
        .select()
        .single();
      if (error) return { success: false, error: error.message };
      setProfile(data);
      if (data.onboarding_complete) setNeedsOnboarding(false);
      return { success: true, data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const logout = useCallback(async () => {
    if (supabase) await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setIsAdmin(false);
    setNeedsOnboarding(false);
  }, []);

  const isEmailConfirmed = profile ? !profile.confirmation_token : true;

  const combinedUser = profile
    ? { ...user, ...profile, name: profile.name || user?.user_metadata?.name || 'User' }
    : user
      ? { ...user, name: user.user_metadata?.name || 'User', avatar: user.user_metadata?.avatar_url || '' }
      : null;

  if (configError) {
    return (
      <AuthContext.Provider value={{
        user: null, profile: null, isAdmin: false, loading: false, needsOnboarding: false,
        login, signup, logout, resetPassword, updatePassword, updateProfile,
        confirmEmail, resendConfirmation, isEmailConfirmed: true,
        isAuthenticated: false, supabase: null, configError: true
      }}>
        {children}
      </AuthContext.Provider>
    );
  }

  return (
    <AuthContext.Provider value={{
      user: combinedUser, profile, isAdmin, loading, needsOnboarding, login, signup, logout,
      resetPassword, updatePassword, updateProfile,
      confirmEmail, resendConfirmation, isEmailConfirmed,
      isAuthenticated: !!user, supabase, configError: false
    }}>
      {children}
    </AuthContext.Provider>
  );
};